import { CommonActions } from '@react-navigation/native';
import { router, useLocalSearchParams, useNavigationContainerRef } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { ActivityIndicator, View, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native';
import { SceneMap, TabBar, TabView } from 'react-native-tab-view';

import { useToast } from '@fhs/auth/src/hooks';
import {
  ActionSheet,
  ActionSheetScreenHeader,
  FadeView,
  IconStarRateFill,
  MediaQueriesContext,
  Skeleton,
  Text,
  mobileMediaQueries,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { useBoxRef } from '@fhs/utils';
import {
  ShortCodeState,
  useInRestaurantRedemptionContext,
} from '@fhs-legacy/frontend/src/state/loyalty/in-restaurant-redemption';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';

import { DisplayCard } from './display-card';
import { EarnTab } from './earn-tab';
import { InRestaurantRedemption } from './in-restaurant-redemption';
import { RedeemTab } from './redeem-tab';
import {
  PersistedRewardTierStatus,
  useValidatePersistedRewardTier,
} from './use-validate-persisted-reward-tier';

const renderScene = SceneMap({
  first: EarnTab,
  second: RedeemTab,
});

const tabRoutes = [
  { key: 'first', title: 'Earn' },
  { key: 'second', title: 'Redeem' },
];

const renderTabBar = props => (
  <TabBar
    {...props}
    indicatorStyle={styles.tabBarIndicator}
    activeColor={tokens.colors.$houseRed}
    inactiveColor={tokens.colors.$houseDark}
    renderLabel={({ color, route }) => (
      <Text.Ui style={[styles.tabBarLabel, { color }]} size="lg" weight="semibold">
        {route.title}
      </Text.Ui>
    )}
    style={styles.tabBar}
  />
);

function ErrorToast() {
  const toast = useToast();
  const { shortCodeState, resetInRestaurantRedemption } = useInRestaurantRedemptionContext();
  const resetInRestaurantRedemptionRef = useBoxRef(resetInRestaurantRedemption);
  useEffect(() => {
    if (shortCodeState === ShortCodeState.RequestError) {
      resetInRestaurantRedemptionRef.current();
      setTimeout(() => {
        toast.show({
          text: "Couldn't retrieve your code.",
          variant: 'negative',
        });
      }, 100);
    }
  }, [shortCodeState, toast, resetInRestaurantRedemptionRef]);
  return null;
}

function DisplayCardHeader() {
  const navigationRef = useNavigationContainerRef();
  const { loyaltyUserReady, loyaltyUser } = useLoyaltyLegacyStates();

  return (
    <DisplayCard
      image={
        <View style={styles.iconContainer}>
          <IconStarRateFill size={24} color={tokens.colors.$houseYellow} />
        </View>
      }
      style={styles.displayCard}
      button={{
        label: 'Learn More',
        onPress: () => {
          // Pop Scan and land on History in one update. Dismissing first focuses
          // Rewards while its stack is still empty, which flashes a blank screen.
          navigationRef.current?.dispatch(
            CommonActions.navigate({
              name: '(tabs)',
              pop: true,
              params: {
                screen: 'rewards',
                params: {
                  state: {
                    index: 0,
                    routes: [{ name: 'history', params: { direct: true } }],
                  },
                },
              },
            })
          );
        },
      }}
    >
      {!loyaltyUserReady ? (
        <Skeleton style={styles.skeleton} />
      ) : (
        <>
          <Text.Ui size="xs">You've got:</Text.Ui>
          <Text.Ui size="md" weight="semibold">
            {loyaltyUser?.points?.toLocaleString()} points
          </Text.Ui>
        </>
      )}
    </DisplayCard>
  );
}

export function InStoreRedeemScreen() {
  const { tab: selectedTab } = useLocalSearchParams<{ tab?: 'earn' | 'redeem' }>();
  const [index, setIndex] = useState(selectedTab === 'redeem' ? 1 : 0);
  const isRedeemTabEnabled = useFlag('enable-in-store-rewards-redeem');

  const { inStoreRewardTierId, resetInRestaurantRedemption } = useInRestaurantRedemptionContext();
  const resetInRestaurantRedemptionRef = useBoxRef(resetInRestaurantRedemption);

  // Revalidate a persisted reward tier against the latest incentives so a stale
  // or non-redeemable selection can't route straight to the QR/order-selection.
  const persistedTierStatus = useValidatePersistedRewardTier();

  // If redeem is disabled but there's a selected reward tier, reset it
  useEffect(() => {
    if (!isRedeemTabEnabled && inStoreRewardTierId) {
      resetInRestaurantRedemptionRef.current();
    }
  }, [isRedeemTabEnabled, inStoreRewardTierId, resetInRestaurantRedemptionRef]);

  const hasPersistedTier = isRedeemTabEnabled && Boolean(inStoreRewardTierId);
  const showRedemption =
    hasPersistedTier && persistedTierStatus === PersistedRewardTierStatus.Valid;
  const isValidatingRedemption =
    hasPersistedTier && persistedTierStatus === PersistedRewardTierStatus.Validating;

  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);
  const containerStyle = isDesktop ? { height: screenHeight } : ({ height: '100%' } as const);
  const drawerProps = useMemo(() => ({ style: { flex: 1 } }), []);

  const onClose = useCallback(() => {
    router.dismiss();
  }, []);

  return (
    <ActionSheet
      onRequestClose={onClose}
      isVisible
      onClose={onClose}
      containerStyle={containerStyle}
      ignoreBottomSafeArea
      drawerProps={drawerProps}
    >
      <MediaQueriesContext.Provider value={mobileMediaQueries}>
        <ActionSheetScreenHeader title="Scan In Restaurant" style={styles.header} />
        {showRedemption ? (
          <FadeView style={{ flex: 1 }}>
            <InRestaurantRedemption />
          </FadeView>
        ) : isValidatingRedemption ? (
          <View style={styles.validatingContainer}>
            <ActivityIndicator size="large" color={tokens.colors.$houseRed} />
          </View>
        ) : (
          <>
            <DisplayCardHeader />
            <TabView
              navigationState={{ index, routes: tabRoutes }}
              renderScene={renderScene}
              renderTabBar={renderTabBar}
              onIndexChange={setIndex}
              initialLayout={{ width: screenWidth }}
              style={{ flex: 1 }}
            />
          </>
        )}
      </MediaQueriesContext.Provider>
      <ErrorToast />
    </ActionSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    borderBottomWidth: 0,
  },
  displayCard: {
    marginBottom: 12,
    marginHorizontal: 12,
  },
  tabBar: {
    backgroundColor: tokens.colors.$white,
  },
  tabBarLabel: {
    paddingVertical: 8,
  },
  tabBarIndicator: {
    backgroundColor: tokens.colors.$houseRed,
  },
  iconContainer: {
    borderRadius: 100,
    backgroundColor: tokens.colors.$blackOpacity04,
    padding: 8,
  },
  skeleton: {
    width: '50%',
    height: 20,
  },
  validatingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
